function bool(value, fallback = false) {
  if (value === true) return true;
  if (value === false) return false;
  return fallback;
}

function lower(value) {
  return String(value || '').trim().toLowerCase();
}

function readBoolean(source, keys, fallback = undefined) {
  for (const key of keys) {
    if (source && source[key] === true) return true;
    if (source && source[key] === false) return false;
  }
  return fallback;
}

export function normalizeModelCapabilities(model = {}) {
  const catalogCaps = model?.catalog?.capabilities || {};
  const topLevelCaps = model || {};
  const rawCaps = model?.capabilities || {};
  const modalities = model?.modalities || model?.catalog?.modalities || {};
  const inputModalities = Array.isArray(modalities?.input)
    ? modalities.input.map((m) => lower(m))
    : [];

  const isEmbedding = model?.isEmbedding === true || model?.is_embedding === true || model?.modelType === 'embedding' || model?.model_type === 'embedding';
  const modelType = isEmbedding ? 'embedding' : 'llm';

  const acceptsText = bool(
    readBoolean(topLevelCaps, ['acceptsText', 'textInput']),
    bool(rawCaps.acceptsText, bool(rawCaps.textInput, bool(catalogCaps.acceptsText, inputModalities.includes('text') || !isEmbedding)))
  );
  const acceptsImages = bool(
    readBoolean(topLevelCaps, ['acceptsImages', 'imageInput']),
    bool(rawCaps.acceptsImages, bool(rawCaps.imageInput, bool(catalogCaps.acceptsImages, inputModalities.includes('image'))))
  );
  const acceptsPdf = bool(
    readBoolean(topLevelCaps, ['acceptsPdf', 'attachment']),
    bool(rawCaps.acceptsPdf, bool(rawCaps.attachment, bool(catalogCaps.acceptsPdf, inputModalities.includes('file'))))
  );
  const acceptsVideo = bool(
    readBoolean(topLevelCaps, ['acceptsVideo', 'videoInput']),
    bool(rawCaps.acceptsVideo, bool(rawCaps.videoInput, bool(catalogCaps.acceptsVideo, inputModalities.includes('video'))))
  );
  const acceptsAudio = bool(
    readBoolean(topLevelCaps, ['acceptsAudio', 'audioInput']),
    bool(rawCaps.acceptsAudio, bool(rawCaps.audioInput, bool(catalogCaps.acceptsAudio, inputModalities.includes('audio'))))
  );
  const isReasoningModel = bool(
    readBoolean(topLevelCaps, ['isReasoningModel', 'reasoning']),
    bool(rawCaps.isReasoningModel, bool(rawCaps.reasoning, bool(catalogCaps.isReasoningModel, false)))
  );
  const canToolCall = bool(
    readBoolean(topLevelCaps, ['canToolCall', 'toolCall', 'can_tool_call', 'tool_call']),
    bool(
      readBoolean(rawCaps, ['canToolCall', 'toolCall', 'can_tool_call', 'tool_call']),
      bool(readBoolean(catalogCaps, ['canToolCall', 'toolCall', 'can_tool_call', 'tool_call']), false)
    )
  );

  return {
    acceptsText,
    acceptsImages,
    acceptsPdf,
    acceptsVideo,
    acceptsAudio,
    isReasoningModel,
    canToolCall,
    modelType,
    isEmbedding,
  };
}

export function getModelCapabilityItems(model = {}) {
  const caps = normalizeModelCapabilities(model);
  return [
    { key: 'acceptsText', label: 'Accepts Text', icon: 'fa-font', enabled: caps.acceptsText },
    { key: 'acceptsImages', label: 'Accepts Images', icon: 'fa-image', enabled: caps.acceptsImages },
    { key: 'acceptsPdf', label: 'Accepts PDF', icon: 'fa-file-pdf', enabled: caps.acceptsPdf },
    { key: 'acceptsVideo', label: 'Accepts Video', icon: 'fa-video', enabled: caps.acceptsVideo },
    { key: 'acceptsAudio', label: 'Accepts Audio', icon: 'fa-volume-high', enabled: caps.acceptsAudio },
    { key: 'isReasoningModel', label: 'Is Reasoning Model', icon: 'fa-brain', enabled: caps.isReasoningModel },
    { key: 'canToolCall', label: 'Can Tool Call', icon: 'fa-wrench', enabled: caps.canToolCall },
  ];
}

export function isModelType(model = {}, expected = 'llm') {
  const caps = normalizeModelCapabilities(model);
  if (expected === 'embedding') return caps.modelType === 'embedding';
  if (expected === 'llm') return caps.modelType !== 'embedding';
  return true;
}

export function inferAttachmentType(file = {}) {
  const name = lower(file?.name || file?.filename || '');
  const mime = lower(file?.mime || file?.type || file?.mime_type || '');

  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('video/')) return 'video';
  if (mime === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';

  if (name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.gif') || name.endsWith('.webp')) return 'image';
  if (name.endsWith('.mp3') || name.endsWith('.wav') || name.endsWith('.ogg') || name.endsWith('.m4a')) return 'audio';
  if (name.endsWith('.mp4') || name.endsWith('.mov') || name.endsWith('.avi') || name.endsWith('.webm')) return 'video';
  if (name.endsWith('.pdf')) return 'pdf';

  return 'text';
}

export function canModelAcceptFile(model = {}, file = {}) {
  const caps = normalizeModelCapabilities(model);
  const fileType = inferAttachmentType(file);
  if (fileType === 'image') return caps.acceptsImages;
  if (fileType === 'audio') return caps.acceptsAudio;
  if (fileType === 'video') return caps.acceptsVideo;
  if (fileType === 'pdf') return caps.acceptsPdf;
  return caps.acceptsText;
}
